import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Mail, Phone, MessageSquare, User, Clock, Eye, ArrowRight } from 'lucide-react';
import chatIcon from '../../assets/img3d/chat.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunicationsPage = () => {
  const navigate = useNavigate();
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommunications();
  }, []);

  const fetchCommunications = async () => {
    try {
      const response = await axios.get(`${API}/admin/communications/recent?limit=50`);
      const commsData = response.data;
      setCommunications(Array.isArray(commsData) ? commsData : []);
    } catch (error) {
      console.error('Error fetching communications:', error);
      setCommunications([]);
    } finally {
      setLoading(false);
    }
  };

  const getCommunicationIcon = (type) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'phone':
        return <Phone className="h-4 w-4" />;
      case 'chat':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    
    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    if (diffMinutes < 1440) return `Il y a ${Math.floor(diffMinutes / 60)} h`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPartyColor = (party) => {
    switch (party) {
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'professional':
        return 'bg-purple-100 text-purple-800';
      case 'seeker':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout
      headerConfig={{
        icon: chatIcon,
        iconColor: 'bg-white',
        title: 'Communications',
        subtitle: 'Historique de toutes les communications',
        primaryAction: 'Tableau de Bord',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/admin'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF9F68]"></div>
        </div>
      ) : communications.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-600">Aucune communication pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {communications.map((item, index) => {
            const comm = item.communication;
            return (
              <Card 
                key={index}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/contact-requests/${item.request_id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {item.seeker_name}
                        </h3>
                        <Badge className={getPartyColor(comm.from_party)}>
                          <User className="h-3 w-3 mr-1" />
                          {comm.from_party === 'admin' ? 'Admin' : 
                           comm.from_party === 'professional' ? 'Professionnel' : 'Chercheur'}
                        </Badge>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <Badge className={getPartyColor(comm.to_party)}>
                          {comm.to_party === 'admin' ? 'Admin' : 
                           comm.to_party === 'professional' ? 'Professionnel' : 'Chercheur'}
                        </Badge>
                        <div className="flex items-center gap-1 text-gray-500">
                          {getCommunicationIcon(comm.type)}
                          <span className="text-sm capitalize">{comm.type}</span>
                        </div>
                      </div>

                      {/* Subject */}
                      {comm.subject && (
                        <div className="font-medium text-gray-900 mb-2">
                          {comm.subject}
                        </div>
                      )}

                      {/* Message Preview */}
                      <p className="text-sm text-gray-700 line-clamp-2 mb-3">
                        {comm.message}
                      </p>

                      {/* Timestamp */}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />
                        {formatDate(comm.date)}
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/contact-requests/${item.request_id}`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Voir la demande
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </Layout>
  );
};

export default CommunicationsPage;
